/**
 * ============================================================================
 * OBJECTS STORE - TRANSLATION KEY DUPLICATION
 * ============================================================================
 * 
 * Utilities for duplicating translation keys when copying objects.
 * 
 * Why This Matters:
 * When you duplicate an object, you need NEW translation keys (string_ids).
 * If you reuse the same string_ids, editing translations in the copy
 * would affect the original object.
 * 
 * @module objects/utils/duplicateKeys
 */

import { createString } from "../../stringsStore";
import { getCachedStringsItems } from "../../stringsStore";
import type { SectionDataObject, TranslatableValue, ToastFunction } from "../types";

// ============================================================================
// OBJECT TRANSLATION KEY DUPLICATION
// ============================================================================

/**
 * Duplicate all translation keys in an object's data structure.
 * 
 * This is ESSENTIAL when duplicating objects to ensure translation keys
 * are not shared between the original and the copy.
 * 
 * Process:
 * 1. Finds all string_ids in the data structure
 * 2. Creates new string records with the same content
 * 3. Returns updated data with new string_ids
 * 
 * @param data - The section data array from an object
 * @param gameId - The game ID for creating new strings
 * @param toast - Optional toast function for error reporting
 * @returns The updated data structure with new string_ids
 * 
 * @example
 * ```ts
 * // When duplicating an object
 * const duplicatedData = await duplicateTranslationKeys(
 *   originalObject.data,
 *   gameId,
 *   toast
 * );
 * 
 * const newObject = await createObject({
 *   ...originalObject,
 *   name: `${originalObject.name} (Copy)`,
 *   data: duplicatedData,
 * });
 * ```
 */
export async function duplicateTranslationKeys(
  data: SectionDataObject[],
  gameId: string,
  toast?: ToastFunction
): Promise<SectionDataObject[]> {
  console.log('[duplicateTranslationKeys] Starting duplication...');
  
  try {
    // Get the current strings to look up source data
    const stringsItems = getCachedStringsItems(gameId);
    const stringsMap = new Map(stringsItems.map(s => [s.id, s]));
    
    // Deep clone the data structure
    const updatedData = JSON.parse(JSON.stringify(data)) as SectionDataObject[];
    
    // Track mappings from old string_id to new string_id
    const stringIdMap = new Map<string, string>();
    
    // Process each section and field
    for (const section of updatedData) {
      for (const field of section.fields) {
        // Skip non-translatable fields
        if (!field.translation) {
          continue;
        }
        
        // ====================================================================
        // HANDLE SINGLE TRANSLATABLE FIELDS (LEGACY FORMAT)
        // ====================================================================
        if (field.string_id && !field.multiple) {
          const originalString = stringsMap.get(field.string_id);
          if (originalString) {
            // Create a new string with the same content but pending status
            const newString = await createString({
              game_id: gameId,
              source_text: originalString.source_text,
              section: originalString.section,
              context: originalString.context,
              metadata: originalString.metadata,
              abbrev: originalString.abbrev,
              status: 'pending' // New keys should be pending
            }, toast);
            
            field.string_id = newString.id;
            stringIdMap.set(field.string_id, newString.id);
            console.log(`  ✓ Duplicated: ${field.string_id} → ${newString.id}`);
          }
        }
        
        // ====================================================================
        // HANDLE MULTIPLE TRANSLATABLE FIELDS (TranslatableValue[])
        // ====================================================================
        if (field.translation && field.multiple && Array.isArray(field.value)) {
          const updatedValues: TranslatableValue[] = [];
          
          for (const item of field.value) {
            if (item && typeof item === 'object' && 'string_id' in item && item.string_id) {
              const originalString = stringsMap.get(item.string_id);
              if (originalString) {
                const newString = await createString({
                  game_id: gameId,
                  source_text: originalString.source_text,
                  section: originalString.section,
                  context: originalString.context,
                  metadata: originalString.metadata,
                  abbrev: originalString.abbrev,
                  status: 'pending'
                }, toast);
                
                updatedValues.push({
                  text: item.text,
                  string_id: newString.id
                });
                
                stringIdMap.set(item.string_id, newString.id);
                console.log(`  ✓ Duplicated array item: ${item.string_id} → ${newString.id}`);
              } else {
                // If original string not found, keep the item as-is
                updatedValues.push(item);
              }
            } else {
              // Non-translatable item or missing string_id, keep as-is
              updatedValues.push(item);
            }
          }
          
          // Update the field value with new string_ids
          field.value = updatedValues as any;
        }
      }
    }
    
    console.log(`[duplicateTranslationKeys] Duplicated ${stringIdMap.size} keys`);
    return updatedData;
    
  } catch (error) {
    console.error('[duplicateTranslationKeys] Error:', error);
    if (toast) {
      toast({
        title: 'Error',
        description: 'Failed to duplicate translation keys',
        variant: 'destructive',
      });
    }
    throw error;
  }
}

// ============================================================================
// DIALOG TRANSLATION KEY DUPLICATION
// ============================================================================

/**
 * Duplicate all translation keys in a dialog's data structure.
 * 
 * This is ESSENTIAL when duplicating dialogs to ensure translation keys
 * are not shared between the original and the copy.
 * 
 * @param dialogData - The dialog data content containing sections with fields
 * @param gameId - The game ID for creating new strings
 * @param toast - Optional toast function for error reporting
 * @returns The updated dialog data with new string_ids
 * 
 * @example
 * ```ts
 * const duplicatedDialogData = await duplicateDialogTranslationKeys(
 *   originalDialog.data,
 *   gameId,
 *   toast
 * );
 * ```
 */
export async function duplicateDialogTranslationKeys(
  dialogData: any, // DialogDataContent type
  gameId: string,
  toast?: ToastFunction
): Promise<any> {
  console.log('[duplicateDialogTranslationKeys] Starting duplication...');
  
  try {
    // Get the current strings to look up source data
    const stringsItems = getCachedStringsItems(gameId);
    const stringsMap = new Map(stringsItems.map(s => [s.id, s]));
    
    // Deep clone the data structure
    const updatedData = JSON.parse(JSON.stringify(dialogData));
    
    // Track mappings from old string_id to new string_id
    const stringIdMap = new Map<string, string>();
    
    if (!updatedData.sections || !Array.isArray(updatedData.sections)) {
      return updatedData;
    }
    
    // Process each section
    for (const section of updatedData.sections) {
      if (!section.fields || !Array.isArray(section.fields)) {
        continue;
      }
      
      // Process each field
      for (const field of section.fields) {
        // ====================================================================
        // DUPLICATE FIELD STRING
        // ====================================================================
        if (field.string_id && field.translatable) {
          const originalString = stringsMap.get(field.string_id);
          if (originalString) {
            const newString = await createString({
              game_id: gameId,
              source_text: originalString.source_text,
              section: originalString.section,
              context: originalString.context,
              metadata: originalString.metadata,
              abbrev: originalString.abbrev,
              status: 'pending'
            }, toast);
            
            field.string_id = newString.id;
            stringIdMap.set(field.string_id, newString.id);
            console.log(`  ✓ Duplicated field: ${field.string_id} → ${newString.id}`);
          }
        }
        
        // ====================================================================
        // DUPLICATE OPTION STRINGS (FOR QUESTION FIELDS)
        // ====================================================================
        if (field.options && Array.isArray(field.options)) {
          for (const option of field.options) {
            if (option.string_id && option.translatable) {
              const originalString = stringsMap.get(option.string_id);
              if (originalString) {
                const newString = await createString({
                  game_id: gameId,
                  source_text: originalString.source_text,
                  section: originalString.section,
                  context: originalString.context,
                  metadata: originalString.metadata,
                  abbrev: originalString.abbrev,
                  status: 'pending'
                }, toast);
                
                option.string_id = newString.id;
                stringIdMap.set(option.string_id, newString.id);
                console.log(`  ✓ Duplicated option: ${option.string_id} → ${newString.id}`);
              }
            }
          }
        }
      }
    }
    
    console.log(`[duplicateDialogTranslationKeys] Duplicated ${stringIdMap.size} dialog keys`);
    return updatedData;
    
  } catch (error) {
    console.error('[duplicateDialogTranslationKeys] Error:', error);
    if (toast) {
      toast({
        title: 'Error',
        description: 'Failed to duplicate dialog translation keys',
        variant: 'destructive',
      });
    }
    throw error;
  }
}
